import { apiFetch } from '../config/api';
import { debugError } from '../utils/debugLog';
import { REQUEST_TIMEOUT_ANALYTICS } from '../config/constants';

/**
 * Analytics Service
 *
 * Handles all execution analytics API operations.
 * Provides methods for fetching execution trends, errors, performance metrics,
 * cost analysis, template comparison, and provider/model performance.
 */
class AnalyticsService {
  /**
   * Get execution trends
   * @param {Object} options - Query options
   * @param {string} options.period - Time period: 7d, 30d, 90d, or custom (YYYY-MM-DD:YYYY-MM-DD)
   * @param {string} options.template_id - Optional template ID filter
   * @returns {Promise<Object>} Execution trends data
   */
  async getExecutionTrends(options = {}) {
    const { period = '7d', template_id } = options;
    const params = new URLSearchParams({ period });
    if (template_id) {
      params.append('template_id', template_id);
    }

    try {
      const response = await apiFetch(
        `/api/v1/templates/analytics/executions/trends?${params.toString()}`,
        { timeout: REQUEST_TIMEOUT_ANALYTICS }
      );
      return response;
    } catch (error) {
      debugError('Failed to fetch execution trends:', error);
      throw error;
    }
  }

  /**
   * Get error analysis
   * @param {Object} options - Query options
   * @param {string} options.period - Time period
   * @param {string} options.error_code - Optional error code filter
   * @param {string} options.template_id - Optional template ID filter
   * @returns {Promise<Object>} Error analysis data
   */
  async getErrorAnalysis(options = {}) {
    const { period = '7d', error_code, template_id } = options;
    const params = new URLSearchParams({ period });
    if (error_code) {
      params.append('error_code', error_code);
    }
    if (template_id) {
      params.append('template_id', template_id);
    }

    try {
      const response = await apiFetch(
        `/api/v1/templates/analytics/executions/errors?${params.toString()}`,
        { timeout: REQUEST_TIMEOUT_ANALYTICS }
      );
      return response;
    } catch (error) {
      debugError('Failed to fetch error analysis:', error);
      throw error;
    }
  }

  /**
   * Get performance metrics
   * @param {Object} options - Query options
   * @param {string} options.period - Time period
   * @param {string} options.template_id - Optional template ID filter
   * @returns {Promise<Object>} Performance metrics data
   */
  async getPerformanceMetrics(options = {}) {
    const { period = '7d', template_id } = options;
    const params = new URLSearchParams({ period });
    if (template_id) {
      params.append('template_id', template_id);
    }

    try {
      const response = await apiFetch(
        `/api/v1/templates/analytics/executions/performance?${params.toString()}`,
        { timeout: REQUEST_TIMEOUT_ANALYTICS }
      );
      return response;
    } catch (error) {
      debugError('Failed to fetch performance metrics:', error);
      throw error;
    }
  }

  /**
   * Get cost analysis
   * @param {Object} options - Query options
   * @param {string} options.period - Time period
   * @param {string} options.provider - Optional provider filter
   * @param {string} options.template_id - Optional template ID filter
   * @returns {Promise<Object>} Cost analysis data
   */
  async getCostAnalysis(options = {}) {
    const { period = '7d', provider, template_id } = options;
    const params = new URLSearchParams({ period });
    if (provider) {
      params.append('provider', provider);
    }
    if (template_id) {
      params.append('template_id', template_id);
    }

    try {
      const response = await apiFetch(
        `/api/v1/templates/analytics/executions/costs?${params.toString()}`,
        { timeout: REQUEST_TIMEOUT_ANALYTICS }
      );
      return response;
    } catch (error) {
      debugError('Failed to fetch cost analysis:', error);
      throw error;
    }
  }

  /**
   * Get template comparison
   * @param {Object} options - Query options
   * @param {string} options.period - Time period
   * @param {number} options.limit - Maximum number of results (default: 20)
   * @returns {Promise<Object>} Template comparison data
   */
  async getTemplateComparison(options = {}) {
    const { period = '7d', limit = 20 } = options;
    const params = new URLSearchParams({ period, limit: limit.toString() });

    try {
      const response = await apiFetch(
        `/api/v1/templates/analytics/executions/templates/comparison?${params.toString()}`,
        { timeout: REQUEST_TIMEOUT_ANALYTICS }
      );
      return response;
    } catch (error) {
      debugError('Failed to fetch template comparison:', error);
      throw error;
    }
  }

  /**
   * Get provider/model performance
   * @param {Object} options - Query options
   * @param {string} options.period - Time period
   * @param {string} options.provider - Optional provider filter
   * @param {number} options.limit - Maximum number of results (default: 20)
   * @returns {Promise<Object>} Provider/model performance data
   */
  async getProviderModelPerformance(options = {}) {
    const { period = '7d', provider, limit = 20 } = options;
    const params = new URLSearchParams({ period, limit: limit.toString() });
    if (provider) {
      params.append('provider', provider);
    }

    try {
      const response = await apiFetch(
        `/api/v1/templates/analytics/executions/providers-models?${params.toString()}`,
        { timeout: REQUEST_TIMEOUT_ANALYTICS }
      );
      return response;
    } catch (error) {
      debugError('Failed to fetch provider/model performance:', error);
      throw error;
    }
  }
}

const analyticsService = new AnalyticsService();
export default analyticsService;
